import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const VehicleCard = ({ vehicle, onRent }) => {
  const getStatusColor = (status) => {
    const colors = {
      available: '#22c55e',
      rented: '#ef4444',
      maintenance: '#eab308',
      reserved: '#3b82f6'
    };
    return colors?.[status] || '#94a3b8';
  };

  const getStatusLabel = (status) => {
    const labels = {
      available: 'Disponible',
      rented: 'Loué',
      maintenance: 'Maintenance',
      reserved: 'Réservé'
    };
    return labels?.[status] || status;
  };

  return (
    <div className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-2xl hover:border-blue-500/30 transition-all duration-300 group">
      {/* Image */}
      <div className="relative h-56 overflow-hidden">
        <Image
          src={vehicle?.image}
          alt={vehicle?.imageAlt}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <span
            className="px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm"
            style={{
              backgroundColor: `${getStatusColor(vehicle?.status)}20`,
              color: getStatusColor(vehicle?.status)
            }}
          >
            {getStatusLabel(vehicle?.status)}
          </span>
          {vehicle?.vehicleType && (
            <span className="px-3 py-1 bg-slate-900/90 backdrop-blur-sm rounded-full text-xs font-medium text-white">
              {vehicle?.vehicleType}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
          {vehicle?.name}
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          {vehicle?.brand} {vehicle?.model} • {vehicle?.year}
        </p>

        {/* Specs */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <Icon name="Users" size={16} />
            <span>{vehicle?.seats} places</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <Icon name="Settings" size={16} />
            <span>{vehicle?.transmission}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <Icon name="Fuel" size={16} />
            <span>{vehicle?.fuelType}</span>
          </div>
          {vehicle?.mileage && (
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <Icon name="Gauge" size={16} />
              <span>{vehicle?.mileage?.toLocaleString()} km</span>
            </div>
          )}
        </div>

        {/* Features */}
        {vehicle?.features?.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {vehicle?.features?.slice(0, 3)?.map((feature, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-500/10 rounded text-xs text-blue-400"
                >
                  {feature}
                </span>
              ))}
              {vehicle?.features?.length > 3 && (
                <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-xs text-slate-600 dark:text-slate-400">
                  +{vehicle?.features?.length - 3} plus
                </span>
              )}
            </div>
          </div>
        )}

        {/* Price */}
        <div className="border-t border-slate-200 dark:border-white/10 pt-4 mb-4">
          <div className="flex items-baseline justify-between">
            <div>
              <span className="text-2xl font-bold text-blue-400">
                {vehicle?.dailyRate?.toLocaleString()} CDF
              </span>
              <span className="text-sm text-slate-600 dark:text-slate-400 ml-2">/ jour</span>
            </div>
            <span className="text-sm text-slate-600 dark:text-slate-400">
              ${vehicle?.dailyRateUsd}
            </span>
          </div>
        </div>

        {/* Action Button */}
        <Button
          variant="default"
          fullWidth
          iconName="Calendar"
          iconPosition="left"
          onClick={() => onRent(vehicle)}
          disabled={vehicle?.status !== 'available'}
        >
          {vehicle?.status === 'available' ? 'Réserver Maintenant' : 'Non Disponible'}
        </Button>
      </div>
    </div>
  );
};

export default VehicleCard;
